import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { testimonials } from '../mock';

export const Testimonials = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.1 });

  return (
    <section
      id="testimonials"
      ref={containerRef}
      className="py-24 relative overflow-hidden"
      style={{ backgroundColor: 'white' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ color: '#283750' }}
          >
            Trusted by Event
            <br />
            <span style={{ color: '#000' }}>Professionals</span>
          </h2>
          <p
            className="text-xl max-w-3xl mx-auto"
            style={{ color: '#736c64' }}
          >
            See what our customers have to say about their experience with
            EventMaster.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => {
            const cardRef = useRef(null);
            const cardInView = useInView(cardRef, { once: false, amount: 0.5 });

            return (
              <motion.div
                key={testimonial.id}
                ref={cardRef}
                initial={{ opacity: 0, y: 50, rotateY: -15 }}
                animate={{
                  opacity: cardInView ? 1 : 0,
                  y: cardInView ? 0 : 50,
                  rotateY: cardInView ? 0 : -15,
                }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.4, 0, 0.3, 1],
                }}
                whileHover={{
                  y: -10,
                  rotateY: 5,
                  transition: { duration: 0.3 },
                }}
                className="p-8 rounded-2xl relative group cursor-pointer"
                style={{
                  backgroundColor: '#f7f5f2',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                  transformStyle: 'preserve-3d',
                  perspective: '1000px',
                }}
              >
                {/* Quote Icon */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{
                    scale: cardInView ? 1 : 0,
                    rotate: cardInView ? 0 : -180,
                  }}
                  transition={{ duration: 0.6, delay: index * 0.1 + 0.3 }}
                  className="absolute top-4 right-4 opacity-20"
                >
                  <Quote size={48} style={{ color: '#000' }} />
                </motion.div>

                {/* Content */}
                <div className="flex items-center space-x-4 mb-6">
                  <motion.img
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                    style={{
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <div>
                    <h4
                      className="font-bold text-lg"
                      style={{ color: '#283750' }}
                    >
                      {testimonial.name}
                    </h4>
                    <p className="text-sm" style={{ color: '#736c64' }}>
                      {testimonial.role}
                    </p>
                    <p className="text-xs" style={{ color: '#000' }}>
                      {testimonial.company}
                    </p>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{
                        scale: cardInView ? 1 : 0,
                        rotate: cardInView ? 0 : -180,
                      }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.1 + 0.4 + i * 0.05,
                      }}
                    >
                      <Star size={18} fill="#fad24b" style={{ color: '#fad24b' }} />
                    </motion.div>
                  ))}
                </div>

                {/* Testimonial Text */}
                <p
                  className="text-base leading-relaxed"
                  style={{ color: '#283750' }}
                >
                  "{testimonial.content}"
                </p>

                {/* Hover effect border */}
                <motion.div
                  initial={{ scale: 0 }}
                  whileHover={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 rounded-2xl border-2 pointer-events-none"
                  style={{ borderColor: '#000' }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
